import { STATS } from 'state/stats/type';
import { ORG_TIMEZONE_DEFAULT } from './constants';
import { DateTime } from 'luxon';
import { add, isBefore, sub } from 'date-fns';
import CURRENCY from 'utils/currency';

export function timeUnitConverter(unit: STATS.IIntervalType) {
  if (unit === STATS.IIntervalType.DAILY) {
    return 'day';
  }
  if (unit === STATS.IIntervalType.HOURLY) {
    return 'hour';
  }
  if (unit === STATS.IIntervalType.MONTHLY) {
    return 'month';
  }
  if (unit === STATS.IIntervalType.WEEKLY) {
    return 'week';
  }
  return 'day';
}

/**
 * add zero valued data points for the points where no data is available
 * It takes a data object with x, y, and value properties, and fills in the gaps in the x array with
 * zeros
 * @param data - { x: Date[]; y: number[]; value: STATS.ValuePoint[] }
 * @param {string} dateFrom - The start date of the data
 * @param {string} dateTo - The end date of the data you want to fill in.
 * @param {'day' | 'hour' | 'month' | 'week'} timeUnit - 'day' | 'hour' | 'month' | 'week'
 * @param timezone - The timezone of the data.
 * @returns An object with three properties: x, y, and value.
 */
export function fillInTheGapsUtil(
  data: { x: Date[]; y: number[]; value: STATS.ValuePoint[] },
  dateFrom: string,
  dateTo: string,
  timeUnit: 'day' | 'hour' | 'month' | 'week',
  timezone = ORG_TIMEZONE_DEFAULT
) {
  // getting the starting and ending point for the loop to create data points

  const min = DateTime.fromISO(dateFrom, { zone: timezone })
    .startOf(timeUnit)
    .toJSDate();

  const max = DateTime.fromISO(dateTo, { zone: timezone })
    .endOf(timeUnit)
    .toJSDate();

  const assoc: Record<string, number> = {};
  const assocValue: Record<string, STATS.ValuePoint> = {};
  for (let i = 0; i < data.x.length; i += 1) {
    /* Store all in assoc array */
    const index = DateTime.fromJSDate(data.x[i], { zone: 'UTC' }).toISO();
    if (index) {
      assoc[index] = data.y[i];
      assocValue[index] = data.value[i];
    }
  }

  const newX: Date[] = [];
  const newY: number[] = [];
  const valueFilled: STATS.ValuePoint[] = [];

  const isMinDST = DateTime.fromJSDate(min, { zone: timezone }).startOf(
    timeUnit
  ).isInDST;

  const isMaxDST = DateTime.fromJSDate(max, { zone: timezone }).startOf(
    timeUnit
  ).isInDST;

  /* Looping through the dates from the start date to the end date, and adding a zero value for each
  date that is not represented in the data. */
  for (let d = min; isBefore(d, max); d = add(d, { [`${timeUnit}s`]: 1 })) {
    let key = d;
    const isDst = DateTime.fromJSDate(d, { zone: timezone }).startOf(
      timeUnit
    ).isInDST;
    /* Checking if the start and end dates are in DST, and if they are, it is adding an hour to the
    date if it is not in DST, and subtracting an hour if it is in DST. */
    if (isMaxDST && isMinDST) {
      key = !isDst ? add(d, { hours: 1 }) : d;
    } else if (isMinDST) {
      key = !isDst ? add(d, { hours: 1 }) : d;
    } else if (isMaxDST) {
      key = isDst ? sub(d, { hours: 1 }) : d;
    } else {
      key = isDst ? sub(d, { hours: 1 }) : d;
    }

    newX.push(key);

    const keystr = DateTime.fromJSDate(key, { zone: 'UTC' }).toISO() || '';

    /* Checking if the date is represented in the data, and if it is not, it is adding a zero
    value for that date. */
    if (keystr) {
      if (!assoc[keystr]) {
        // const temp: STATS.IDataPointValue = {};
        newY.push(0);
        valueFilled.push({} as STATS.ValuePoint);
      } else {
        newY.push(assoc[keystr]);
        valueFilled.push(assocValue[keystr]);
      }
    }
  }

  return { x: newX, y: newY, value: valueFilled };
}

/**
 * Formats a numeric value into a displayable string based on the provided graph type.
 * If the graph type is 'gross' or 'AOV, the value is converted into $ string;
 * otherwise, the value is returned as a string.
 *
 * @param {STATS.graphType} graphType - The type of the graph, which determines the formatting.
 * @param {number} value - The numeric value to be formatted.
 * @returns {string} The formatted value as a string.
 */
export const formatValueToDisplayStr = (
  graphType: STATS.graphType,
  value: number
) =>
  graphType !== STATS.graphType.payments
    ? CURRENCY.convertToMainUnit(value)
    : String(value);

/**
 * Returns the appropriate metric value based on the graph type.
 * For 'payments', returns the transaction count.
 * For 'gross_amount', returns the gross amount.
 * For 'aov', returns the gross amount divided by the transaction count.
 *
 * @param {STATS.graphType} graphType - The type of the graph determining which value to return.
 * @param {number} [grossAmount=0] - The total gross amount (defaults to 0 if not provided).
 * @param {number} [transactionCount=0] - The number of transactions (defaults to 0 if not provided).
 * @returns {number} The calculated metric value.
 */
export const getMetricValueFromPayType = (
  graphType: STATS.graphType,
  grossAmount = 0,
  transactionCount = 0
) => {
  switch (graphType) {
    case STATS.graphType.payments:
      return transactionCount || 0;
    case STATS.graphType.gross_amount:
      return grossAmount || 0;
    case STATS.graphType.aov:
      return transactionCount ? grossAmount / transactionCount : 0;
  }
};

/**
 * Calculates the percentage share of the given metric type for all payments relative to the total.
 * metric is determined by graphType  (e.g., payments/ gross amount/ AOV).
 * percentage is rounded to one decimal place, unless it's an integer, in which case no decimals are shown.
 * adjusts the largest percentage to ensure the sum of percents equal 100%.
 * @param {STATS.graphType} graphType - determines the metric to be used - payments count/gross amount/AOV
 * @param {STATS.ISummary} summaries - contains metric values of all payment types.
 * @returns {Record<STATS.IPaymentType, string>} An object where the keys are the payment types and the values are the percentage shares, as strings.
 */
export const getChartLegendPercentShares = (
  graphType: STATS.graphType,
  summaries: STATS.ISummary[]
) => {
  const totals = summaries.filter(
    (summ) => summ.payment_type === STATS.IPaymentType.ALL
  )?.[0];
  const totalMetricValue = getMetricValueFromPayType(
    graphType,
    totals?.current?.gross_volume,
    totals?.current?.payments
  );

  const paymentSummaries = summaries.filter(
    (summ) => summ.payment_type !== STATS.IPaymentType.ALL
  );
  // compute percent share for each payment_type
  const percentages = paymentSummaries.reduce(
    (
      acc: Record<STATS.IPaymentType, string>,
      currentSummary: STATS.ISummary
    ) => {
      const currentGraphMetric = getMetricValueFromPayType(
        graphType,
        currentSummary?.current?.gross_volume,
        currentSummary?.current?.payments
      );

      const percentage = totalMetricValue
        ? (currentGraphMetric / totalMetricValue) * 100
        : 0;

      acc[currentSummary.payment_type] =
        percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(1);

      return acc;
    },
    {} as Record<STATS.IPaymentType, string>
  );

  // skip % rounding off for aov graph
  if (graphType === STATS.graphType.aov) {
    percentages[STATS.IPaymentType.ALL] = '100';
    return percentages;
  }

  // compute sum of percentages and sum difference with 100%
  const unroundedSum = Object.values(percentages).reduce(
    (acc, p) => acc + parseFloat(p),
    0
  );
  const roundedSum = Math.round(unroundedSum * 10) / 10;
  const roundingDifference = 100 - roundedSum;

  // adjust the largest percentage to ensure total sum is 100
  if (Math.abs(roundingDifference) > 0) {
    const percentKeys = Object.keys(percentages) as STATS.IPaymentType[];

    const maxPaymentType = percentKeys.reduce(
      (maxKey: STATS.IPaymentType, key: STATS.IPaymentType) => {
        return percentages[key] > percentages[maxKey] ? key : maxKey;
      },
      percentKeys[0]
    );

    percentages[maxPaymentType] = (
      parseFloat(percentages[maxPaymentType]) + roundingDifference
    ).toFixed(1);
  }

  percentages[STATS.IPaymentType.ALL] = '100';

  return percentages;
};
